import { Box, Flex, Grid, Text } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import { useCallback, useMemo, useState } from 'react';
import {
  Button,
  CloseIcon,
  IconButton,
  Paragraph,
  Tooltip,
} from 'ui-components';
import { MagicAniamtedFilledIcon } from 'ui-components/dist/MagicAniamtedFilledIcon';
import { convertCSVtoArray } from '~/core/utils';
import { TalentValues } from '~/screens/AddTalent/ProfileInformation';
import { verticalOptions } from '~/screens/MyAgency/components/Table/Verticals';

export const Verticals = () => {
  const [displayClearSmartData, setDisplayClearSmartData] = useState(false);
  const { values, setFieldValue } = useFormikContext<TalentValues>();
  const { verticals = [] } = values;

  const sPVerticals = values.smartData?.verticals?.value;
  const sPVerticalsLoading =
    (!values.verticals || values.verticals.length === 0) &&
    values.smartData?.verticals?.status === 'pending';
  const isVerticalsFromSmartData = useMemo(
    () =>
      values.verticals && sPVerticals
        ? isEqual(convertCSVtoArray(sPVerticals), values.verticals)
        : false,
    [values.verticals, sPVerticals],
  );

  const handleSelectVerticals = useCallback(
    (name: string) => {
      const isSelected = verticals.includes(name);

      if (isSelected) {
        // Remove from selection
        const array = verticals.filter((item) => item !== name);
        setFieldValue('verticals', array);
        return;
      }

      // Otherwise add to selection
      setFieldValue('verticals', [...verticals, name]);
    },
    [verticals, setFieldValue],
  );

  const Icon = useMemo(() => {
    if (
      !displayClearSmartData &&
      (sPVerticalsLoading || isVerticalsFromSmartData)
    ) {
      return (
        <Flex
          alignItems="center"
          onMouseEnter={() => {
            if (values.verticals?.length) {
              setDisplayClearSmartData(true);
            }
          }}
        >
          <MagicAniamtedFilledIcon
            data-animated={sPVerticalsLoading}
            marginRight={2}
          />
        </Flex>
      );
    }

    if (displayClearSmartData && !!values.verticals?.length) {
      return (
        <Tooltip
          label="Clear Smart Profile info"
          aria-label="Clear Smart Profile info"
          placement="top-end"
        >
          <IconButton
            aria-label="Clear"
            icon={<CloseIcon color="primary.300" />}
            onClick={() => {
              setFieldValue('verticals', []);
              setDisplayClearSmartData(false);
            }}
            onMouseLeave={() => {
              setDisplayClearSmartData(false);
            }}
            size="sm"
            variant="transparent"
            trackId="foam-add-talent-profile-info-verticals-clear"
          />
        </Tooltip>
      );
    }

    return null;
  }, [
    displayClearSmartData,
    sPVerticalsLoading,
    isVerticalsFromSmartData,
    values.verticals,
    setFieldValue,
    setDisplayClearSmartData,
  ]);

  return (
    <Grid gap={2}>
      <Grid templateColumns="1fr auto" alignItems="center" gap={1}>
        <Box marginLeft={3}>
          <Paragraph fontWeight="medium" size="md">
            Select Verticals
          </Paragraph>
          <Paragraph as="span" size="sm">
            You can add up to 3 verticals tags
          </Paragraph>
        </Box>
        {Icon}
      </Grid>
      <Flex gap={2} wrap="wrap">
        {Object.values(verticalOptions).map((v) => {
          const isSelected = verticals.includes(v.name);
          const isDisabled = !isSelected && verticals.length === 3;

          return (
            <Button
              key={v.name}
              variant={isSelected ? 'secondary' : 'tertiary'}
              isDisabled={isDisabled || sPVerticalsLoading}
              onClick={() => handleSelectVerticals(v.name)}
              size="sm"
              trackId={`foam-add-talent-profile-info-vertical-${v.name
                .toLowerCase()
                .replace(/\s+/g, '-')}`}
            >
              <Text fontSize="md">{v.name}</Text>
            </Button>
          );
        })}
      </Flex>
    </Grid>
  );
};
